import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatCurrency, formatNumber, formatDate, statusBadgeClass, statusLabel } from '../utils/formatters';
import { Search, ChevronLeft, ChevronRight, Package, Eye, X, ArrowDownToLine, Calendar } from 'lucide-react';

export default function StockInPage() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', warehouse_id: '', date_from: '', date_to: '' });
  const [warehouses, setWarehouses] = useState([]);
  const [viewingReceipt, setViewingReceipt] = useState(null);

  useEffect(() => {
    fetchReceipts();
  }, [pagination.page, filters]);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const params = queryParams({
        page: pagination.page,
        limit: pagination.limit,
        search: filters.search,
        warehouse_id: filters.warehouse_id,
        start_date: filters.date_from || undefined,
        end_date: filters.date_to || undefined,
      });
      const res = await api.get('/goods-receipts', { params });
      if (res.data.success) {
        setReceipts(res.data.data?.receipts || res.data.data?.items || res.data.data || []);
        setPagination((prev) => ({
          ...prev,
          total: res.data.data?.total || 0,
          totalPages: res.data.data?.totalPages || Math.ceil((res.data.data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const res = await api.get('/warehouses', { params: { limit: 1000 } });
      setWarehouses(res.data.data?.warehouses || res.data.data || []);
    } catch (error) {
      // Silently fail
    }
  };

  const handleViewReceipt = async (receipt) => {
    try {
      const res = await api.get(`/goods-receipts/${receipt.id}`);
      if (res.data.success) {
        setViewingReceipt(res.data.data);
      }
    } catch (error) {
      // Fallback: show basic info
      setViewingReceipt(receipt);
    }
  };

  const totalItems = receipts.reduce((sum, r) => sum + (r.items_count || r.items?.length || 0), 0);
  const totalValue = receipts.reduce((sum, r) => sum + (r.total_amount || r.total || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Stok Masuk</h1>
          <p className="page-subtitle">Riwayat penerimaan barang dan stok masuk</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card">
          <div className="stat-icon stat-icon-brand">
            <ArrowDownToLine className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Total Penerimaan</p>
            <p className="stat-value">{formatNumber(receipts.length)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon-success">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Total Item Diterima</p>
            <p className="stat-value">{formatNumber(totalItems)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon-brand">
            <Package className="h-6 w-6" />
          </div>
          <div>
            <p className="stat-label">Total Nilai Masuk</p>
            <p className="stat-value">{formatCurrency(totalValue)}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Cari nomor dokumen..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && fetchReceipts()}
              />
            </div>
            <div>
              <select
                className="input"
                value={filters.warehouse_id}
                onChange={(e) => setFilters({ ...filters, warehouse_id: e.target.value })}
              >
                <option value="">Semua Gudang</option>
                {warehouses.map((w) => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                className="input pl-9"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                placeholder="Dari tanggal"
              />
            </div>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="date"
                className="input pl-9"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                placeholder="Sampai tanggal"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={fetchReceipts} className="btn-primary btn-sm">Cari</button>
            <button onClick={() => setFilters({ search: '', warehouse_id: '', date_from: '', date_to: '' })} className="btn-secondary btn-sm">Reset</button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-36" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-16" />
                </div>
              ))}
            </div>
          </div>
        ) : receipts.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <ArrowDownToLine className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Data Stok Masuk</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada penerimaan barang yang tercatat.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>No. Dokumen</th>
                    <th>Tanggal</th>
                    <th>Pemasok</th>
                    <th>Gudang</th>
                    <th>Status</th>
                    <th className="text-right">Total</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt) => (
                    <tr key={receipt.id}>
                      <td>
                        <span className="font-medium text-gray-900 dark:text-white">{receipt.doc_number || receipt.number || '-'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(receipt.date || receipt.receipt_date || receipt.created_at)}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{receipt.supplier?.name || receipt.supplier_name || '-'}</span>
                      </td>
                      <td>
                        <span className="text-sm text-gray-700 dark:text-gray-200">{receipt.warehouse?.name || receipt.warehouse_name || '-'}</span>
                      </td>
                      <td>
                        <span className={`badge ${statusBadgeClass(receipt.status)}`}>{statusLabel(receipt.status)}</span>
                      </td>
                      <td className="text-right">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{formatCurrency(receipt.total_amount || receipt.total)}</span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewReceipt(receipt)}
                          className="p-1.5 text-primary-900 hover:bg-primary-50 dark:hover:bg-primary-950/50 rounded"
                          title="Lihat Detail"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} disabled={pagination.page <= 1} className="btn btn-sm btn-outline">
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">Halaman {pagination.page} / {pagination.totalPages}</span>
                  <button onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))} disabled={pagination.page >= pagination.totalPages} className="btn btn-sm btn-outline">
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* View Detail Modal */}
      {viewingReceipt && (
        <div className="modal-overlay">
          <div className="modal-content max-w-2xl">
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800 flex items-center justify-between">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Detail Penerimaan Barang</h3>
              <button onClick={() => setViewingReceipt(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">No. Dokumen</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingReceipt.doc_number || viewingReceipt.number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Tanggal</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatDate(viewingReceipt.date || viewingReceipt.receipt_date || viewingReceipt.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Pemasok</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingReceipt.supplier?.name || viewingReceipt.supplier_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Gudang</p>
                  <p className="font-medium text-gray-900 dark:text-white">{viewingReceipt.warehouse?.name || viewingReceipt.warehouse_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Status</p>
                  <span className={`badge ${statusBadgeClass(viewingReceipt.status)}`}>{statusLabel(viewingReceipt.status)}</span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
                  <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(viewingReceipt.total_amount || viewingReceipt.total)}</p>
                </div>
              </div>
              {viewingReceipt.items && viewingReceipt.items.length > 0 && (
                <div>
                  <p className="label">Item Penerimaan</p>
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Nama Produk</th>
                          <th className="text-right">Jumlah</th>
                          <th className="text-right">Harga Satuan</th>
                          <th className="text-right">Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewingReceipt.items.map((item, idx) => (
                          <tr key={item.id || idx}>
                            <td className="font-mono text-xs">{item.sku || item.product?.sku || '-'}</td>
                            <td>{item.product_name || item.product?.name || '-'}</td>
                            <td className="text-right">{formatNumber(item.quantity)}</td>
                            <td className="text-right">{formatCurrency(item.unit_price || item.price)}</td>
                            <td className="text-right font-medium">{formatCurrency(item.subtotal || (item.quantity || 0) * (item.unit_price || item.price || 0))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {viewingReceipt.notes && (
                <div>
                  <p className="label">Catatan</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{viewingReceipt.notes}</p>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <button onClick={() => setViewingReceipt(null)} className="btn-secondary">Tutup</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
